import { Component, DestroyRef, inject, Input, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { TranslateService } from '@ngx-translate/core';
import { MessageService } from 'primeng/api';
import { environment } from '../../../../environments/environment';
import { TranslationService } from '../../../services/translation.service';
import { ImageDto, NewImageDto } from '../../../types/dtos/image.dto';
import { InputImageGalleryInterface } from '../../../types/interfaces/input.interface';
import { ButtonIconComponent } from '../button-icon/button-icon.component';

@Component({
  selector: 'app-image-edit-gallery',
  imports: [
    ButtonIconComponent,
  ],
  templateUrl: './image-edit-gallery.html',
  styleUrl: './image-edit-gallery.css',
})
export class ImageEditGallery implements OnInit {
  public readonly imagePath: string = window.location.protocol + '//' + window.location.hostname + environment.imageUrl;
  @Input()
  public formData!: InputImageGalleryInterface;
  public newFilePreviews: NewImageDto[] = [];
  private readonly _destroyRef: DestroyRef = inject(DestroyRef);

  constructor(
    private readonly _domSanitizer: DomSanitizer,
    private readonly _messageService: MessageService,
    private readonly _translateService: TranslateService,
  ) {
  }

  private get _imagesControl() {
    return this.formData.formGroup?.get(this.formData.id);
  }

  private get _imagesValue(): {
    currentImages: ImageDto[];
    newImages: File[];
    deletedImageIds: number[];
  } {
    return this._imagesControl?.value ?? {
      currentImages: [],
      newImages: [],
      deletedImageIds: [],
    };
  }

  public ngOnInit(): void {
    this._imagesControl?.valueChanges
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((value) => {
        if (!value?.newImages?.length) {
          this.revokePreviewUrls();
        }
      });
  }

  public removeImage(index: number): void {
    const currentImages = [...this._imagesValue.currentImages];
    currentImages[index] = {
      ...currentImages[index],
      pendingDelete: true,
    };

    this._imagesControl?.setValue({
      ...this._imagesValue,
      currentImages,
    });

    this._syncForm();
  }

  public cancelDeleteImage(index: number): void {
    const currentImages = [...this._imagesValue.currentImages];
    currentImages[index] = {
      ...currentImages[index],
      pendingDelete: false,
    };

    this._imagesControl?.setValue({
      ...this._imagesValue,
      currentImages,
    });

    this._syncForm();
  }

  public removeImageFromNew(url: string): void {
    const preview: NewImageDto | undefined = this.newFilePreviews.find((file) => file.url === url);

    if (preview) {
      URL.revokeObjectURL(preview.url);
    }

    this.newFilePreviews = this.newFilePreviews.filter((file) => file.url !== url);
    this._syncForm();
  }

  public isFileSizeExceeded(file: File): boolean { // 15MB
    return file.size > 1024 * 1024 * 15;
  }

  public isImage(file: File): boolean {
    return file.type.startsWith('image/');
  }

  public async onFileChange(event: Event): Promise<void> {
    this.formData.formGroup?.disable();
    const input = event.target as HTMLInputElement;
    let files: File[] = input.files ? Array.from(input.files) : [];

    this.revokePreviewUrls();

    files = files.filter((file: File) => {
      if (!this.isImage(file)) {
        this._messageService.add({
          severity: 'error',
          summary: this._translateService.instant('common.messages.error.image.not-supported-summary'),
          detail: this._translateService.instant('common.messages.error.image.not-supported', {type: file.type}),
        })

        return false
      }

      if (this.isFileSizeExceeded(file)) {
        this._messageService.add({
          severity: 'error',
          summary: this._translateService.instant('common.messages.error.image.too-large-summary'),
          detail: this._translateService.instant('common.messages.error.image.too-large'),
        })

        return false
      }

      // Max file Size

      return true
    });

    this.newFilePreviews = files.map((file: File) => ({
      file,
      url: URL.createObjectURL(file),
    }));

    await Promise.all(
      this.newFilePreviews.map(preview =>
        this.waitForImage(preview.url),
      ),
    );

    this._syncForm();

    if (this.formData.onInput) {
      this.formData.onInput(event);
    }

    this.formData.formGroup?.enable();
  }

  public bypassUrl(imageUrl: string): SafeUrl {
    return this._domSanitizer.bypassSecurityTrustUrl(imageUrl);
  }

  private waitForImage(url: string): Promise<void> {
    return new Promise((resolve, reject) => {
      const img = new Image();

      img.onload = () => resolve();
      img.onerror = reject;

      img.src = url;
    });
  }

  private revokePreviewUrls(): void {
    for (const preview of this.newFilePreviews) {
      URL.revokeObjectURL(preview.url);
    }

    this.newFilePreviews = [];
  }

  private _syncForm(): void {
    const control = this._imagesControl;

    if (!control) return;

    control.setValue({
      currentImages: this._imagesValue.currentImages,
      newImages: this.newFilePreviews.map((preview) => preview.file),
      deletedImageIds: this._imagesValue.currentImages
        .filter((image) => image.pendingDelete)
        .map((image) => image.id),
    });

    control.markAsDirty();
    control.updateValueAndValidity();
  }
}
